import { faCalendarRange } from '@fortawesome/pro-regular-svg-icons';
import { chain, maxBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import DataTableToolbarButton from '../../../../voxel/DataTableToolbar/DataTableToolbarButton';
import { KeywordTableColumn, KeywordTableColumnTypes } from '../KeywordTableColumnPicker/models';
import { KeywordTableState } from '../logic/useKeywordTableState';

export interface VolumeTrendButtonProps {
  keywordTableState: KeywordTableState;
}

const VolumeTrendButton: React.FC<VolumeTrendButtonProps> = ({ keywordTableState }) => {
  const [volumeTrendActive, setVolumeTrendActive] = useState(false);
  useEffect(() => {
    const month12 = keywordTableState.columnPickerBag.columnState.some(
      (cs) => cs.order != undefined && cs.type === KeywordTableColumnTypes.VolumeLast12months
    );
    const peakMonth = keywordTableState.columnPickerBag.columnState.some(
      (cs) => cs.order != undefined && cs.type === KeywordTableColumnTypes.VolumeMonthPeak
    );
    const peakQuarter = keywordTableState.columnPickerBag.columnState.some(
      (cs) => cs.order != undefined && cs.type === KeywordTableColumnTypes.VolumeQuarterPeak
    );
    if (month12 && peakMonth && peakQuarter) setVolumeTrendActive(true);
    else setVolumeTrendActive(false);
  }, [keywordTableState.columnPickerBag.columnState]);

  const reorderColumns = (
    column: KeywordTableColumn,
    prev: KeywordTableColumn[],
    maxOrder: number
  ) => {
    return chain(prev)
      .map((c) => {
        if (c.id === column.id && c.type === column.type)
          return { ...c, order: c.order === undefined ? maxOrder : undefined };
        return c;
      })
      .orderBy((c) => c === undefined || c.order)
      .map((c, index) => {
        if (c.order === undefined) return c;
        if (c.order === index) return c;
        return { ...c, order: index };
      })
      .value();
  };

  const handleShowTrendColumns = (columnList: KeywordTableColumn[]) => {
    // First remove columns
    handleHideTrendColumns(columnList);
    // Then add them back in group
    columnList.map((column) => {
      keywordTableState.columnPickerBag.setColumnState((prev) => {
        const maxOrder = maxBy(prev, (c) => c.order)?.order ?? 0;
        return reorderColumns(column, prev, maxOrder);
      });
    });
  };

  const handleHideTrendColumns = (columnList: KeywordTableColumn[]) => {
    columnList.map((column) => {
      keywordTableState.columnPickerBag.setColumnState((prev) => {
        return reorderColumns(column, prev, undefined);
      });
    });
  };

  const toggleActiveState = () => {
    if (volumeTrendActive) {
      handleHideTrendColumns([
        keywordTableState.columnPickerBag.columnState.find(
          (c) => c.type === KeywordTableColumnTypes.VolumeQuarterPeak
        ),
        keywordTableState.columnPickerBag.columnState.find(
          (c) => c.type === KeywordTableColumnTypes.VolumeMonthPeak
        ),
        keywordTableState.columnPickerBag.columnState.find(
          (c) => c.type === KeywordTableColumnTypes.VolumeLast12months
        ),
      ]);
    } else {
      handleShowTrendColumns([
        keywordTableState.columnPickerBag.columnState.find(
          (c) => c.type === KeywordTableColumnTypes.VolumeQuarterPeak
        ),
        keywordTableState.columnPickerBag.columnState.find(
          (c) => c.type === KeywordTableColumnTypes.VolumeMonthPeak
        ),
        keywordTableState.columnPickerBag.columnState.find(
          (c) => c.type === KeywordTableColumnTypes.VolumeLast12months
        ),
      ]);
    }
  };

  return (
    <DataTableToolbarButton
      tooltip="Toggle seasonal analysis columns"
      toggle={volumeTrendActive}
      iconProp={faCalendarRange}
      onClick={() => toggleActiveState()}
    />
  );
};

export default VolumeTrendButton;
